import React, { useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Globe, Search, X, GraduationCap, Quote, Sparkles, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { ProofBar } from '../components/ProofBar';
import { analytics } from '../hooks/useAnalytics';
import { MENTORS, SCHOOLS, MAJOR_BUCKETS, bucketForMentor } from '../data/mentors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL = '__all__';

// Deterministic ISO-week index → rotates the featured mentor once per week.
function getFeaturedMentor() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diffDays = Math.floor((now - start) / 86400000);
  const weekIndex = Math.floor(diffDays / 7) + now.getFullYear();
  return MENTORS[weekIndex % MENTORS.length];
}

// Pick two anonymized mentors that best fit the visitor's picks.
// Tier: school + major → school only → major only → any two (deterministic-ish).
function getMatchedMentors({ school_preference, major_bucket }, exclude = [], n = 2) {
  const excludeSet = new Set(exclude);
  const pool = MENTORS.filter((m) => !excludeSet.has(m.initials));
  const strict = pool.filter((m) => (
    (!school_preference || m.school === school_preference) &&
    (!major_bucket || bucketForMentor(m) === major_bucket)
  ));
  const bySchool = pool.filter((m) => school_preference && m.school === school_preference);
  const byMajor = pool.filter((m) => major_bucket && bucketForMentor(m) === major_bucket);
  const tiers = [strict, bySchool, byMajor, pool];
  const picked = [];
  const seen = new Set();
  for (const tier of tiers) {
    for (const m of tier) {
      if (seen.has(m.initials)) continue;
      picked.push(m);
      seen.add(m.initials);
      if (picked.length >= n) return picked;
    }
  }
  return picked;
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide border transition-all ${
        active
          ? 'bg-[#0F392B] text-white border-[#0F392B] shadow-sm'
          : 'bg-white text-[#0F392B] border-[#E2E2E2] hover:border-[#0F392B]/40'
      }`}
    >
      {children}
    </button>
  );
}

function MentorCard({ m }) {
  return (
    <div
      className="rounded-2xl border border-[#E2E2E2] bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
      data-testid={`mentor-card-${m.initials.replace(/\./g, '')}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-[#0F392B] text-[#D4AF37] flex items-center justify-center text-[11px] font-bold tracking-wide">
          {m.initials}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-medium text-[#D4AF37]">Mentor</p>
          <p className="text-[11px] text-muted-foreground">Class of {m.classYear}</p>
        </div>
      </div>
      <p className="text-base font-semibold text-[#0F392B] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
        {m.school}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{m.major}</p>

      {m.quote && (
        <div className="mt-4 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] p-4 relative" data-testid={`mentor-quote-${m.initials.replace(/\./g, '')}`}>
          <Quote className="absolute top-2 left-2 h-3 w-3 text-[#D4AF37]/60" strokeWidth={2} />
          <p className="text-[12px] text-foreground leading-relaxed italic pl-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            &ldquo;{m.quote}&rdquo;
          </p>
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-medium mt-2 pl-4">— Why I mentor</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#E2E2E2] space-y-2">
        <div className="flex items-center gap-1.5">
          <Globe className="h-3 w-3 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-[11px] text-muted-foreground">{m.hometown}</p>
        </div>
        <div className="flex items-start gap-1.5">
          <GraduationCap className="h-3 w-3 text-muted-foreground mt-0.5" strokeWidth={1.75} />
          <p className="text-[11px] text-muted-foreground leading-snug">{m.focus}</p>
        </div>
      </div>
    </div>
  );
}

function FeaturedMentor({ mentor, onMatchClick }) {
  return (
    <section className="py-14 md:py-16 bg-[#0F392B] relative overflow-hidden" data-testid="featured-mentor-section">
      <div className="absolute top-0 right-0 w-72 h-72 bg-[#D4AF37]/8 rounded-full translate-x-1/3 -translate-y-1/2" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-[#D4AF37]" strokeWidth={2} />
          <p className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase">Mentor of the Week</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center bg-white/5 border border-white/10 rounded-3xl p-6 md:p-10 backdrop-blur-sm" data-testid={`featured-mentor-${mentor.initials.replace(/\./g, '')}`}>
          {/* Avatar block */}
          <div className="lg:col-span-2 flex flex-col items-center lg:items-start text-center lg:text-left">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center text-2xl font-bold tracking-wide mb-5">
              {mentor.initials}
            </div>
            <p className="text-3xl font-semibold text-white leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {mentor.school}
            </p>
            <p className="text-sm text-white/70 mb-4">{mentor.major} · Class of {mentor.classYear}</p>
            <div className="flex flex-col items-center lg:items-start gap-1.5 text-xs text-white/60">
              <div className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" strokeWidth={1.75} />{mentor.hometown}</div>
              <div className="flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" strokeWidth={1.75} />{mentor.focus}</div>
            </div>
          </div>

          {/* Quote block */}
          <div className="lg:col-span-3">
            <Quote className="h-6 w-6 text-[#D4AF37] mb-3" strokeWidth={2} />
            <p className="text-xl md:text-2xl text-white leading-relaxed italic mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              &ldquo;{mentor.quote}&rdquo;
            </p>
            <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-6">— Why I mentor</p>
            <Button
              onClick={onMatchClick}
              className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-11 px-6 text-sm font-semibold"
              data-testid="featured-mentor-match-cta"
            >
              Get matched to a mentor like this <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function MentorMatchForm({ innerRef }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    grade: '',
    school_preference: '',
    major_bucket: '',
    brief: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matches, setMatches] = useState([]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.brief.trim().length < 10) {
      toast.error('Tell us a bit more about your situation (at least 10 characters).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/mentor-match/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          grade: form.grade || undefined,
          school_preference: form.school_preference || undefined,
          major_bucket: form.major_bucket || undefined,
          brief: form.brief,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong. Please try again.');
      const picks = getMatchedMentors({
        school_preference: form.school_preference,
        major_bucket: form.major_bucket,
      });
      setMatches(picks);
      setSubmitted(true);
      toast.success('Brief received — a matched mentor will reach out within 48 hours.');
    } catch (err) {
      toast.error(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section ref={innerRef} className="py-16 md:py-20 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="mentor-match-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Mentor Match</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Tell us who you&apos;d want to work with.
          </h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto leading-relaxed">
            Share a short brief. A matched mentor from our bench reaches out within <span className="text-[#0F392B] font-medium">48 hours</span> — no cost, no obligation.
          </p>
        </div>

        {submitted ? (
          <div className="space-y-6" data-testid="mentor-match-success">
            <div className="bg-white border border-[#E2E2E2] rounded-2xl p-10 text-center shadow-sm">
              <CheckCircle2 className="h-10 w-10 text-[#0F392B] mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Brief received.</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                A matched mentor will reach out to <span className="text-foreground font-medium">{form.email}</span> within 48 hours. If it&apos;s urgent, book a strategy call while you wait.
              </p>
              <div className="mt-6">
                <Link to="/consultation" onClick={() => analytics.ctaStrategyCall('mentor-match-success')}>
                  <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-6 text-sm font-semibold" data-testid="mentor-match-success-call-cta">
                    Book a Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {matches.length > 0 && (
              <div data-testid="mentor-match-suggestions">
                <div className="text-center mb-6">
                  <p className="text-[10px] font-semibold text-[#D4AF37] tracking-widest uppercase mb-2">Preview</p>
                  <h4 className="text-lg md:text-xl font-medium text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Mentors from the bench who fit your brief
                  </h4>
                  <p className="text-xs text-muted-foreground mt-2 max-w-lg mx-auto">
                    These are two anonymized examples of the kind of mentor we&apos;d shortlist for you. Your matched mentor may be one of them — or a closer fit from our full bench.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {matches.map((m, i) => (
                    <MentorCard key={m.initials} m={m} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#E2E2E2] rounded-2xl p-6 md:p-8 shadow-sm space-y-5" data-testid="mentor-match-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">Your name *</Label>
                <Input required value={form.name} onChange={update('name')} placeholder="Student or parent name" className="h-11 border-[#E2E2E2]" data-testid="match-input-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">Email *</Label>
                <Input required type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" className="h-11 border-[#E2E2E2]" data-testid="match-input-email" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">Phone (optional)</Label>
                <Input value={form.phone} onChange={update('phone')} placeholder="+1 555 555 5555" className="h-11 border-[#E2E2E2]" data-testid="match-input-phone" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">Grade / Stage</Label>
                <select value={form.grade} onChange={update('grade')} className="w-full h-11 rounded-md border border-[#E2E2E2] bg-white px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20" data-testid="match-select-grade">
                  <option value="">Select…</option>
                  <option value="9th">9th grade</option>
                  <option value="10th">10th grade</option>
                  <option value="11th">11th grade</option>
                  <option value="12th">12th grade</option>
                  <option value="transfer">Transfer applicant</option>
                  <option value="parent">Parent (student TBD)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">School preference</Label>
                <select value={form.school_preference} onChange={update('school_preference')} className="w-full h-11 rounded-md border border-[#E2E2E2] bg-white px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20" data-testid="match-select-school">
                  <option value="">Open to any</option>
                  {SCHOOLS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">Major direction</Label>
                <select value={form.major_bucket} onChange={update('major_bucket')} className="w-full h-11 rounded-md border border-[#E2E2E2] bg-white px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20" data-testid="match-select-major">
                  <option value="">Open to any</option>
                  {MAJOR_BUCKETS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-[#0F392B] mb-1.5 block">A short brief *</Label>
              <Textarea
                required
                value={form.brief}
                onChange={update('brief')}
                placeholder="Tell us about the student — current profile, target schools, and what you'd want the mentor to help most with."
                rows={5}
                maxLength={800}
                className="border-[#E2E2E2] resize-none"
                data-testid="match-textarea-brief"
              />
              <p className="text-[11px] text-muted-foreground mt-1 text-right">{form.brief.length}/800</p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-[#E2E2E2]">
              <p className="text-[11px] text-muted-foreground italic">
                We reply within 48 hours. Your information is never shared outside our team.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-7 text-sm font-semibold w-full sm:w-auto"
                data-testid="match-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <>Request my mentor match <Send className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}


export default function MentorsPage() {
  const [school, setSchool] = useState(ALL);
  const [bucket, setBucket] = useState(ALL);
  const [query, setQuery] = useState('');
  const matchFormRef = useRef(null);
  const featured = useMemo(() => getFeaturedMentor(), []);

  const scrollToMatch = () => {
    if (matchFormRef.current) {
      matchFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MENTORS.filter((m) => {
      if (school !== ALL && m.school !== school) return false;
      if (bucket !== ALL && bucketForMentor(m) !== bucket) return false;
      if (q) {
        const hay = `${m.school} ${m.major} ${m.hometown} ${m.focus} ${m.quote || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [school, bucket, query]);

  const reset = () => {
    setSchool(ALL);
    setBucket(ALL);
    setQuery('');
  };

  const anyFilter = school !== ALL || bucket !== ALL || query.trim() !== '';

  return (
    <div className="min-h-screen bg-white" data-testid="mentors-page">
      <SEO
        title="Mentor Bench — Current Ivy League Student Mentors"
        description="Browse our anonymized roster of current Ivy League and Ivy Plus student mentors. Filter by school and major. Every Elite engagement is matched to a mentor in your target field."
        keywords="ivy league mentor, harvard student mentor, ivy league admissions mentor, college application mentor, current ivy league student"
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Ivy League Consulting Mentor Bench",
          "description": "Anonymized roster of current Ivy League and Ivy Plus student mentors.",
          "numberOfItems": MENTORS.length,
          "itemListElement": MENTORS.map((m, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Person",
              "name": `${m.initials} — ${m.school} '${String(m.classYear).slice(-2)}`,
              "jobTitle": `Mentor · ${m.major}`,
              "affiliation": { "@type": "CollegeOrUniversity", "name": m.school },
              "homeLocation": m.hometown,
              "description": m.quote,
            },
          })),
        }}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">The Mentor Bench</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Current Ivy League students,<br />
            <span className="italic text-[#D4AF37]">on a mission.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Every Elite client is paired with a current Ivy League or Ivy Plus student mentor — matched by major, target schools, and background. Browse the anonymized roster below.
          </p>
        </div>
      </section>

      <ProofBar location="mentors-hero" />

      {/* Featured Mentor of the Week */}
      <FeaturedMentor mentor={featured} onMatchClick={scrollToMatch} />

      {/* Filters + Grid */}
      <section className="py-14 md:py-20" data-testid="mentors-directory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filter Panel */}
          <div className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-5 md:p-6 mb-10" data-testid="mentor-filters">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1">Filter the bench</p>
                <h2 className="text-lg font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Find a mentor by school or major
                </h2>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search school, major, hometown…"
                  className="pl-9 h-10 bg-white border-[#E2E2E2] text-sm"
                  data-testid="mentor-search-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">School</p>
                <div className="flex flex-wrap gap-2">
                  <FilterPill active={school === ALL} onClick={() => setSchool(ALL)} testid="school-filter-all">
                    All schools
                  </FilterPill>
                  {SCHOOLS.map((s) => (
                    <FilterPill
                      key={s}
                      active={school === s}
                      onClick={() => setSchool(s)}
                      testid={`school-filter-${s.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    >
                      {s}
                    </FilterPill>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">Major</p>
                <div className="flex flex-wrap gap-2">
                  <FilterPill active={bucket === ALL} onClick={() => setBucket(ALL)} testid="major-filter-all">
                    All majors
                  </FilterPill>
                  {MAJOR_BUCKETS.map((b) => (
                    <FilterPill
                      key={b.id}
                      active={bucket === b.id}
                      onClick={() => setBucket(b.id)}
                      testid={`major-filter-${b.id}`}
                    >
                      {b.label}
                    </FilterPill>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E2E2]">
              <p className="text-xs text-muted-foreground" data-testid="mentor-count">
                Showing <span className="text-[#0F392B] font-semibold">{filtered.length}</span> of {MENTORS.length} mentors
              </p>
              {anyFilter && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#0F392B] hover:text-[#0F392B]/70"
                  data-testid="mentor-reset-filters"
                >
                  <X className="h-3 w-3" /> Reset filters
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="mentors-empty-state">
              <p className="text-base text-[#0F392B] font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                No mentors match those filters.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Our bench grows every semester. Reset filters, or book a call and tell us exactly who you&apos;d want to work with.
              </p>
              <Button onClick={reset} variant="outline" className="border-[#0F392B] text-[#0F392B]" data-testid="mentor-empty-reset">
                Reset filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="mentors-grid">
              {filtered.map((m, i) => <MentorCard key={i} m={m} />)}
            </div>
          )}

          <p className="text-[11px] text-muted-foreground text-center mt-10 italic max-w-2xl mx-auto">
            Mentor identities are anonymized to protect current-student privacy. Full names, academic records, and background are shared with matched clients during onboarding.
          </p>
        </div>
      </section>

      {/* Mentor Match Form */}
      <MentorMatchForm innerRef={matchFormRef} />

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="mentors-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get matched to the right mentor.
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Every Elite engagement includes a named Ivy student mentor in your target major. Book a confidential strategy call to discuss the match.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/consultation" onClick={() => analytics.ctaStrategyCall('mentors-page')}>
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="mentors-cta-strategy-call">
                Book a Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/consulting#elite" onClick={() => analytics.ctaEliteApply('mentors-page')}>
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="mentors-cta-elite">
                Apply for Elite Consulting
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
